/* eslint-disable no-empty */
/* eslint-disable no-unused-vars */
import { log, warn, error } from '@/utils/debugLogger';

export async function getImageDimensionsAsync(url) {
	return fetch(url)
		.then(response => response.blob())
		.then(blob => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.onload = () => {
					resolve({ width: img.width, height: img.height });
					URL.revokeObjectURL(img.src); // Clean up object URL
				};
				img.onerror = reject;
				img.src = URL.createObjectURL(blob);
			});
		});
}

/**
 * Maps pixel coordinates from an equirectangular 360-degree image to yaw and pitch angles.
 *
 * @param {number} x - The x-coordinate (horizontal pixel position).
 * @param {number} y - The y-coordinate (vertical pixel position).
 * @param {number} imageWidth - The width of the image in pixels.
 * @param {number} imageHeight - The height of the image in pixels.
 * @returns {{ yaw: number, pitch: number }} An object containing yaw and pitch angles in degrees.
 */
export function pixelToYawPitch(x, y, imageWidth, imageHeight) {
    // Input validation
    x = Number(x);
    y = Number(y);
    imageWidth = Number(imageWidth);
    imageHeight = Number(imageHeight);

    if (isNaN(x) || isNaN(y) || isNaN(imageWidth) || isNaN(imageHeight)) {
        throw new Error('Invalid input: x, y, imageWidth, and imageHeight must be numbers.');
    }

    // Clamping to ensure x and y are within image bounds
    const clampedX = Math.max(0, Math.min(x, imageWidth - 1));
    const clampedY = Math.max(0, Math.min(y, imageHeight - 1));

    // Normalization
    const relativeX = clampedX / imageWidth;
    const relativeY = clampedY / imageHeight;

    // Mapping to angles
    const yaw = (relativeX - 0.5) * 360;
    const pitch = (0.5 - relativeY) * 180;

    return { yaw, pitch };
}

export function resetInspectHotspots() {
	const canvas = document.getElementsByClassName('inspect-canvas');
	for (let i = 0; i < canvas.length; i++) {
		canvas[i].remove();
	}
}

export function updateInspectHotspots(viewer, width, height, inspectItems) {
    function drawLines() {
        // Remove existing canvases
        document.querySelectorAll('.inspect-canvas').forEach(canvas => canvas.remove());

        inspectItems.forEach((item, index) => {
            let hotspots = document.querySelectorAll(`.inspect-hotspot.rec-${index}`);
            if (hotspots.length !== 4) return;

            let positions = Array.from(hotspots).map(hotspot => {
                if(!hotspot.style.transform){
                    
                    return {x:0 , y:0}
                }
                let coordinates = hotspot.style.transform.split('(')[1].split(')')[0].split(',');
                let x = Number.parseFloat(coordinates[0].split('p')[0]);
                let y = Number.parseFloat(coordinates[1].split('p')[0]);
                return { x, y };
            });

            // Find the bounding box of the hotspots
            let minX = Math.min(...positions.map(p => p.x));
            let maxX = Math.max(...positions.map(p => p.x));
            let minY = Math.min(...positions.map(p => p.y));
            let maxY = Math.max(...positions.map(p => p.y));

            // Create canvas
            let canvas = document.createElement('canvas');
            canvas.id = `inspect-${index}-canvas`;
            canvas.classList.add('inspect-canvas');
            canvas.width = maxX - minX;
            canvas.height = maxY - minY;
            canvas.style.position = 'absolute';
            canvas.style.top = minY + 'px';
            canvas.style.left = minX + 'px';
            canvas.style.zIndex = '1';
            
            let ctx = canvas.getContext('2d');
            
            // Draw the box with increased thickness
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4; // Increased thickness
            ctx.beginPath();
            positions.forEach((pos, i) => {
                let x = pos.x - minX;
                let y = pos.y - minY;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.stroke();

            document.getElementById('inspect-viewer').appendChild(canvas);
        });
    }

    try {
        resetInspectHotspots();

        inspectItems.forEach((item, index) => {
            const { centerPoint, imageWidth, imageHeight } = item;
            const padding = 0.2; // 20% padding
            const paddedWidth = imageWidth * (1 + 2 * padding);
            const paddedHeight = imageHeight * (1 + 2 * padding);
            const halfWidth = paddedWidth / 2;
            const halfHeight = paddedHeight / 2;
    
            // Calculate the four corners with padding
            const corners = [
                { x: centerPoint.x - halfWidth, y: centerPoint.y - halfHeight }, // Top-left
                { x: centerPoint.x + halfWidth, y: centerPoint.y - halfHeight }, // Top-right
                { x: centerPoint.x + halfWidth, y: centerPoint.y + halfHeight }, // Bottom-right
                { x: centerPoint.x - halfWidth, y: centerPoint.y + halfHeight }  // Bottom-left
            ];
    
            // Add hotspots for each corner
            corners.forEach((corner, cornerIndex) => {
                const { yaw, pitch } = pixelToYawPitch(corner.x, corner.y, width, height);
                let id = `inspect-${index}-corner-${cornerIndex}`;
                viewer.removeHotSpot(id);
                try {
                    viewer.addHotSpot({
                        type: 'info',
                        pitch,
                        yaw,
                        text: `Inspect ${index + 1} - Corner ${cornerIndex + 1}`,
                        cssClass: `inspect-hotspot rec-${index} corner-${cornerIndex}`,
                        id: id,
                    });
                } catch (error) {}
            });
        });
    
        // Function to draw lines between hotspots
      
        drawLines();
    }
    catch(err){
        log(err)
    }
    
}


export function orderPointsToFormPolygon(points) {
	// Calculate the centroid of the points
	const centroid = points.reduce((acc, point) => {
		acc.x += point.x;
		acc.y += point.y;
		return acc;
	}, { x: 0, y: 0 });

	centroid.x /= points.length;
	centroid.y /= points.length;

	// Sort points by angle with respect to the centroid
	const sortedPoints = points.slice().sort((a, b) => {
		const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
		const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
		return angleA - angleB;
	});

	// Ensure the polygon is closed by adding the first point at the end
	sortedPoints.push(sortedPoints[0]);

	return sortedPoints;
}

